/**
 * Tradução do catálogo de erros (§50, SPEC-014 §10).
 *
 * A tela escolhe a mensagem pelo `code`, nunca pela `message` do servidor: o
 * código é contrato estável, a mensagem é auxiliar e pode mudar. É também o que
 * garante que nenhum detalhe técnico chegue ao usuário.
 */

const MESSAGES: Record<string, string> = {
  AUTHENTICATION_ERROR: 'E-mail ou senha inválidos.',
  AUTH_RATE_LIMITED: 'Muitas tentativas de acesso. Aguarde alguns minutos e tente de novo.',
  FORBIDDEN: 'Você não tem acesso a este recurso.',

  VALIDATION_ERROR: 'Confira os dados informados.',
  INVALID_SEARCH: 'Revise os termos da pesquisa.',
  INVALID_DATE_RANGE: 'O período informado é inválido.',
  NOT_FOUND: 'Não encontramos o que você procura.',
  CONFLICT: 'Esta ação não é possível no estado atual.',

  RATE_LIMITED: 'Muitas requisições em pouco tempo. Aguarde um instante.',
  SEARCH_LIMIT_REACHED: 'O limite de coleta do mês foi atingido. Fale com o administrador.',

  PROVIDER_RATE_LIMIT: 'O provedor de dados atingiu o limite. Tente novamente mais tarde.',
  PROVIDER_TIMEOUT: 'O provedor de dados demorou para responder.',
  PROVIDER_UNAVAILABLE: 'O provedor de dados está indisponível no momento.',
  // Não é o provedor que está fora do ar — é o alvo que não pôde ser lido
  // (SPEC-024). A mensagem diz o que fazer, porque esta é acionável.
  PROVIDER_TARGET_UNAVAILABLE:
    'O perfil ou página informado não pôde ser lido. Ele pode estar privado, removido ou bloqueado — confira o link.',
  UNSUPPORTED_OPERATION: 'Esta busca não é suportada para a plataforma escolhida.',

  EXPORT_FAILED: 'Não foi possível gerar a exportação.',
  EXPORT_NOT_READY: 'A exportação ainda está sendo gerada.',
  EXPORT_EXPIRED: 'Este arquivo expirou. Gere a exportação novamente.',
  EXPORT_EMPTY: 'Não há resultados neste recorte para exportar. Ajuste os filtros.',
  JOB_CANCELLED: 'Esta pesquisa foi cancelada.',

  NETWORK_ERROR: 'Sem conexão com o servidor. Verifique sua rede.',
  INTERNAL_ERROR: 'Algo deu errado do nosso lado. Tente novamente.',
  SERVICE_UNAVAILABLE: 'O serviço está temporariamente indisponível. Tente em instantes.',
};

const FALLBACK = 'Algo deu errado. Tente novamente.';

export interface ApiErrorBody {
  code: string;
  message?: string;
  details?: Record<string, unknown>;
  requestId?: string | null;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | null;

  constructor(status: number, body: ApiErrorBody) {
    super(messageForCode(body.code));
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.requestId = body.requestId ?? null;
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  /** Sessão expirada ou ausente: a aplicação precisa voltar ao login. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

export function messageForCode(code: string): string {
  return MESSAGES[code] ?? FALLBACK;
}

/** Mensagem amigável para qualquer erro capturado, inclusive os inesperados. */
export function describeError(error: unknown): string {
  if (ApiError.is(error)) return error.message;
  return FALLBACK;
}

/**
 * Detalhes de validação vindos do servidor, quando existirem — permite apontar
 * o campo problemático em vez de só dizer "confira os dados".
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!ApiError.is(error)) return {};

  const fields = error.details?.fields;
  if (!Array.isArray(fields)) return {};

  const mapped: Record<string, string> = {};
  for (const item of fields) {
    if (
      typeof item === 'object' &&
      item !== null &&
      typeof (item as { path?: unknown }).path === 'string' &&
      typeof (item as { message?: unknown }).message === 'string'
    ) {
      const entry = item as { path: string; message: string };
      mapped[entry.path] = entry.message;
    }
  }
  return mapped;
}
